import { Dispatch, SetStateAction, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { textStyle } from '../../styles/mixins/_commonPageContentStyles';

import BrandingStyleguide from './Tabs/BrandingStyleguide';
import ChartMatch from './Tabs/ChartMatch';
import DashboardExamples from './Tabs/DashboardExamples';
import DataVisualization from './Tabs/DataVisualization';
import DevResources from './Tabs/DevResources';
import GeneralComponents from './Tabs/GeneralComponents';
import GetStarted from './Tabs/GetStarted';
import { Menus, SubMenus } from './types';

type NavigationProps = {
  className?: string;
  isMenuClosed: boolean;
  setIsMenuClosed: Dispatch<SetStateAction<boolean>>;
};

export const DEFAULT_COLLAPSED_MENUS = [
  Menus.GET_STARTED,
  Menus.BRANDING_STYLEGUIDE,
  Menus.GENERAL_COMPONENTS,
  Menus.DATA_VISUALISATION,
  Menus.DEV_RESOURCES,
  Menus.CHART_MATCH,
];

export const DEFAULT_COLLAPSED_SUB_MENUS = [
  SubMenus.LIENS,
  SubMenus.BARS,
  SubMenus.MAPS,
  SubMenus.TABLES,
  SubMenus.CIRCULARS,
  SubMenus.DIAGRAMS,
  SubMenus.OTHERS,
];

// The section of the current route starts open: deriving it here, not in an effect after the first
// render, keeps it from collapsing and then animating open on every page load.
const initialCollapsed = (pathname: string) => {
  const [section, subSection] = pathname.split('/').filter(Boolean);

  return {
    menus: DEFAULT_COLLAPSED_MENUS.filter((item) => item !== (section ?? Menus.GET_STARTED)),
    subMenus: section
      ? DEFAULT_COLLAPSED_SUB_MENUS.filter((item) => item !== subSection)
      : DEFAULT_COLLAPSED_SUB_MENUS,
  };
};

const Navigation = ({ className = '', isMenuClosed, setIsMenuClosed }: NavigationProps) => {
  const location = useLocation();
  const [collapsedMenus, setCollapsedMenus] = useState<string[]>(
    () => initialCollapsed(location.pathname).menus,
  );
  const [collapsedSubMenus, setCollapsedSubMenus] = useState<string[]>(
    () => initialCollapsed(location.pathname).subMenus,
  );

  const handleMenuCollapse = (menu: string, hasSubMenus = false) => {
    if (hasSubMenus) {
      setCollapsedSubMenus((prevState) => {
        if (!prevState.includes(menu)) return [...prevState, menu];

        return DEFAULT_COLLAPSED_SUB_MENUS.filter((item) => item !== menu);
      });
    } else {
      setCollapsedMenus((prevState) => {
        if (!prevState.includes(menu)) return [...prevState, menu];

        return DEFAULT_COLLAPSED_MENUS.filter((item) => item !== menu);
      });
    }
  };

  return (
    <div className={className}>
      <ul className="lvl-1">
        <GetStarted
          isMenuClosed={isMenuClosed}
          setIsMenuClosed={setIsMenuClosed}
          handleMenuCollapse={handleMenuCollapse}
          collapsedMenus={collapsedMenus}
        />
        <BrandingStyleguide
          collapsedMenus={collapsedMenus}
          handleMenuCollapse={handleMenuCollapse}
          isMenuClosed={isMenuClosed}
          setIsMenuClosed={setIsMenuClosed}
        />
        <GeneralComponents
          isMenuClosed={isMenuClosed}
          setIsMenuClosed={setIsMenuClosed}
          handleMenuCollapse={handleMenuCollapse}
          collapsedMenus={collapsedMenus}
        />
        <DataVisualization
          isMenuClosed={isMenuClosed}
          setIsMenuClosed={setIsMenuClosed}
          collapsedMenus={collapsedMenus}
          collapsedSubMenus={collapsedSubMenus}
          handleMenuCollapse={handleMenuCollapse}
        />
        <ChartMatch isMenuClosed={isMenuClosed} setIsMenuClosed={setIsMenuClosed} />
        <DashboardExamples isMenuClosed={isMenuClosed} setIsMenuClosed={setIsMenuClosed} />
        <DevResources
          isMenuClosed={isMenuClosed}
          setIsMenuClosed={setIsMenuClosed}
          collapsedMenus={collapsedMenus}
          handleMenuCollapse={handleMenuCollapse}
        />
      </ul>
    </div>
  );
};

const NavigationStyled = styled(Navigation)`
  ul {
    list-style: none;
    margin: 0;
    padding-left: 0;
  }

  button {
    cursor: pointer;
  }

  .lvl-1 {
    > li {
      > button,
      > a {
        border: 0 none;
        padding: 0.75rem 1rem;
        display: flex;
        gap: 0.5rem;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        border-radius: 0.375rem;
        ${textStyle('actions14Sb')}
        text-decoration: none;
        transition: background 0.3s ease-in-out;

        div {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          transition: color 0.3s ease-in-out;

          svg {
            transition: color 0.3s ease-in-out;
          }
        }

        &:not(.active) {
          background-color: transparent;

          /* The accordion chevron sits directly under the link: without a colour it takes the browser link blue. */
          > svg {
            color: ${({ theme }) => theme.neutralTextWeak};
          }

          &:active {
            svg {
              color: ${({ theme }) => theme.neutralTextWeak};
            }
          }
          div {
            color: ${({ theme }) => theme.neutralTextWeak};

            svg {
              color: ${({ theme }) => theme.neutralTextWeak};
            }
          }

          &:hover {
            background-color: ${({ theme }) => theme.neutralBackgroundHover};

            > svg {
              color: ${({ theme }) => theme.neutralText};
            }

            div {
              color: ${({ theme }) => theme.neutralText};

              svg {
                color: ${({ theme }) => theme.neutralText};
              }
            }
          }
        }

        &.active {
          background-color: ${({ theme }) => theme.neutralBackgroundBase};

          > svg {
            color: ${({ theme }) => theme.neutralText};
          }

          div {
            color: ${({ theme }) => theme.primaryText};

            svg {
              color: ${({ theme }) => theme.primaryText};
            }
          }

          &:hover {
            div {
              color: ${({ theme }) => theme.neutralText};

              svg {
                color: ${({ theme }) => theme.neutralText};
              }
            }
          }
        }
      }
    }
  }

  button {
    background: transparent;
    border: 0 none;
    cursor: pointer;
    display: flex;
    width: 100%;
  }

  .lvl-2 {
    max-height: 130rem;
    overflow: hidden;
    padding-bottom: 0.75rem;
    padding-top: 1rem;
    transition:
      max-height 0.1s ease-in-out,
      padding 0.1s ease-in-out;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    &.collapsed {
      max-height: 0;
      padding-bottom: 0;
      padding-top: 0;
    }

    > li {
      margin-left: 2rem;
      padding-right: 1rem;
      position: relative;

      > button,
      > a {
        border: 0 none;
        padding: 0.5rem 0.75rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        ${textStyle('actions12Sb')}
        text-decoration: none;
        width: 100%;
        border-radius: 0.375rem;
        transition:
          background 0.3s ease-in-out,
          color 0.3s ease-in-out;

        &::before,
        &::after {
          transition: background 0.3s ease-in-out;
        }

        > svg {
          transition: color 0.3s ease-in-out;
        }

        &:not(.active) {
          background-color: transparent;
          color: ${({ theme }) => theme.neutralTextWeak};

          > svg {
            color: ${({ theme }) => theme.neutralTextWeak};
          }

          &:hover {
            background-color: ${({ theme }) => theme.neutralBackgroundHover};
            color: ${({ theme }) => theme.neutralText};

            > svg {
              color: ${({ theme }) => theme.neutralText};
            }
          }
        }

        &.active {
          background-color: ${({ theme }) => theme.primaryBackground};
          color: ${({ theme }) => theme.primaryText};

          > svg {
            color: ${({ theme }) => theme.neutralText};
          }
        }
      }

      &:not(:last-child) {
        &::before {
          content: '';
          height: 100%;
          width: 0.0625rem;
          position: absolute;
          top: 0;
          left: -0.5625rem;
          background-color: ${({ theme }) => theme.neutralBorderMedium};
        }

        > button,
        > a {
          &::before {
            content: '';
            height: 2rem;
            width: 0.0625rem;
            position: absolute;
            top: 0;
            left: -0.5625rem;
          }
          &::after {
            content: '';
            height: 0.0625rem;
            width: 0.5rem;
            top: 1rem;
            left: -0.5rem;
            transform: translateY(-50%);
            position: absolute;
          }

          &:not(.active) {
            &::before,
            &::after {
              background-color: ${({ theme }) => theme.neutralBorderMedium};
            }

            &:hover {
              &::before,
              &::after {
                background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
              }
            }
          }

          &.active {
            cursor: pointer;

            &::before {
              content: '';
              height: 2rem;
              width: 0.0625rem;
              position: absolute;
              top: 0;
              left: -0.5625rem;
            }

            &::before,
            &::after {
              background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
            }
          }
        }
      }

      &:last-child {
        > button,
        > a {
          &::before {
            content: '';
            height: calc(50% + 0.0625rem);
            width: 0.0625rem;
            position: absolute;
            top: 0;
            left: -0.5625rem;
          }

          &::after {
            content: '';
            height: 0.0625rem;
            width: 0.5rem;
            top: 1rem;
            left: -0.5rem;
            transform: translateY(-50%);
            position: absolute;
          }

          &:not(.active) {
            &::before,
            &::after {
              background-color: ${({ theme }) => theme.neutralBorderMedium};
            }

            &:hover {
              &::before,
              &::after {
                background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
              }
            }
          }

          &.active {
            cursor: pointer;

            &::before,
            &::after {
              background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
            }

            &:hover {
              &::before,
              &::after {
                background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
              }
            }
          }
        }
      }
    }

    .lvl-3 {
      padding-top: 0.5rem;
      padding-bottom: 1rem;
      max-height: 130rem;
      overflow: hidden;
      transition:
        max-height 0.1s ease-in-out,
        padding 0.1s ease-in-out;

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }

      &.collapsed {
        max-height: 0;
        padding-bottom: 0;
        padding-top: 0;
      }

      > li {
        margin-left: 1rem;
        position: relative;

        > button,
        > a {
          border: 0 none;
          padding: 0.5rem 0.75rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          ${textStyle('actions12Sb')}
          text-decoration: none;
          width: 100%;
          border-radius: 0.375rem;
          transition:
            background 0.3s ease-in-out,
            color 0.3s ease-in-out;

          &::before,
          &::after {
            transition: background 0.3s ease-in-out;
          }

          > svg {
            transition: color 0.3s ease-in-out;
          }

          &:not(.active) {
            color: ${({ theme }) => theme.neutralTextWeak};

            > svg {
              color: ${({ theme }) => theme.neutralTextWeak};
            }

            &:hover {
              background-color: ${({ theme }) => theme.neutralBackgroundHover};
              color: ${({ theme }) => theme.neutralText};

              > svg {
                color: ${({ theme }) => theme.neutralText};
              }
            }
          }

          &.active {
            color: ${({ theme }) => theme.primaryText};

            > svg {
              color: ${({ theme }) => theme.neutralText};
            }
          }
        }

        &:not(:last-child) {
          &::before {
            content: '';
            height: 100%;
            width: 0.0625rem;
            position: absolute;
            top: 0;
            left: -0.5625rem;
            background-color: ${({ theme }) => theme.neutralBorderMedium};
          }

          > button,
          > a {
            &::before {
              content: '';
              height: 2rem;
              width: 0.0625rem;
              position: absolute;
              top: 0;
              left: -0.5625rem;
            }
            &::after {
              content: '';
              height: 0.0625rem;
              width: 0.5rem;
              top: 1rem;
              left: -0.5rem;
              transform: translateY(-50%);
              position: absolute;
            }

            &:not(.active) {
              &::before,
              &::after {
                background-color: ${({ theme }) => theme.neutralBorderMedium};
              }

              &:hover {
                &::before,
                &::after {
                  background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
                }
              }
            }

            &.active {
              cursor: default;

              &::before {
                content: '';
                height: 2rem;
                width: 0.0625rem;
                position: absolute;
                top: 0;
                left: -0.5625rem;
              }

              &::before,
              &::after {
                background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
              }
            }
          }
        }

        &:last-child {
          > button,
          > a {
            &::before {
              content: '';
              height: calc(50% + 0.0625rem);
              width: 0.0625rem;
              position: absolute;
              top: 0;
              left: -0.5625rem;
            }

            &::after {
              content: '';
              height: 0.0625rem;
              width: 0.5rem;
              top: 1rem;
              left: -0.5rem;
              transform: translateY(-50%);
              position: absolute;
            }

            &:not(.active) {
              &::before,
              &::after {
                background-color: ${({ theme }) => theme.neutralBorderMedium};
              }

              &:hover {
                &::before,
                &::after {
                  background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
                }
              }
            }

            &.active {
              cursor: default;

              &::before,
              &::after {
                background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
              }

              &:hover {
                &::before,
                &::after {
                  background-color: ${({ theme }) => theme.primaryBackgroundStrongHover};
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default NavigationStyled;
